/**
 * Module dependencies.
 */

import { AuthShell } from 'app/components/pages/auth/auth-shell';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Logo } from 'app/components/pages/landing/brand';
import { appRoutes } from 'app/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVerifyEmail } from 'app/hooks/requests/use-verify-email';
import React, { useEffect, useRef } from 'react';

/**
 * Export `VerifyPage` component.
 */

export function VerifyPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const ran = useRef(false);

  const {
    isError,
    isSuccess,
    mutate: verify
  } = useVerifyEmail({
    onSuccess: () => {
      window.setTimeout(() => navigate(appRoutes.dashboard.home.base), 1400);
    }
  });

  useEffect(() => {
    if (ran.current || !token) {
      return;
    }

    ran.current = true;

    verify(token);
  }, [token, verify]);

  const failed = !token || isError;

  return (
    <AuthShell>
      <div className={'flex justify-center'}>
        <Logo size={40} />
      </div>

      {isSuccess ? (
        <>
          <div
            className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl'}
            style={{ backgroundColor: 'rgba(34,165,68,0.12)' }}
          >
            <CheckCircle2 className={'h-7 w-7'} style={{ color: '#1B752B' }} />
          </div>

          <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Email verified'}</h1>

          <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>{'Taking you to your dashboard…'}</p>
        </>
      ) : failed ? (
        <>
          <div className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl bg-neutral-100'}>
            <XCircle className={'h-7 w-7'} style={{ color: '#BE123C' }} />
          </div>

          <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Link expired'}</h1>

          <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
            {'This verification link is invalid or has expired. Sign in to request a new one.'}
          </p>

          <button
            className={
              'mt-8 h-11 w-full rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50'
            }
            onClick={() => navigate(appRoutes.login)}
            type={'button'}
          >
            {'Back to sign in'}
          </button>
        </>
      ) : (
        <>
          <div className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl bg-neutral-100'}>
            <Loader2 className={'h-6 w-6 animate-spin text-neutral-400'} />
          </div>

          <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Verifying your email'}</h1>

          <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>{'Just a moment…'}</p>
        </>
      )}
    </AuthShell>
  );
}
